/**
 * Adds to the product list
 * @param {object} productModel - array of products to add
 */
var createProductsModel = function (productModel) {
    // Any client side model transformation can be centralized here.
    // Example, interpretation of product model flags into actions a user is able to take
    return productModel;
};
/**
 * Main class for the product service and its publicly exposed functions
 * this is responsible for maintaining a search criteria and executing it against the search api to retrieve product
 * lists
 */
var ProductService = /** @class */ (function () {
    function ProductService($http, $cookies, Auth, $q) {
        'ngInject';
        this.productStateWatchers = [];
        this.productErrorWatchers = [];
        this.gotoAlternateProduct = false;
        this.productStates = {
            none: 'NONE',
            started: 'STARTED',
            completed_sucessfully: 'COMPLETED_SUCCESS',
            completed_error: {},
        };
        this.productCurrentRequestState = {
            active: false,
        };
        this.productErrorTypes = {
            error_give_up: 'ERROR_GIVE_UP',
            error_can_try_again: 'ERROR_CAN_TRY_AGAIN',
            error_unknown: 'ERROR_UKNOWN',
        };
        this.$http = $http;
        this.$cookies = $cookies;
        this.Auth = Auth;
        this.$q = $q;
    }
    /**
     * registerSearchStateEvent - Allow consumers to register for event state changes when refreshing data
     * @param {function} notifyCallback - function to call by the consumer when event is triggered
     * @return {function} deregisterSearchStateEvent
     */
    ProductService.prototype.registerProductStateEvent = function (notifyCallback) {
        var self = this;
        // add the observers to our list
        this.productStateWatchers.push(notifyCallback);
        // remove the observers from our list when called, make sure to do this in destructors of anything listening.
        return function deregisterProductStateEvent() {
            var index = self.productStateWatchers.indexOf(notifyCallback);
            if (index !== -1) {
                self.productStateWatchers.splice(index, 1);
            }
        };
    };
    ;
    /**
     * SearchStateEvent - Fires a state change event. Also updates a current state model. Useful for new objects to tap into to see what is going on at init.
     * @param state - State of the search request
     * @param {object} obj - Additional information about the state
     */
    ProductService.prototype.ProductStateEvent = function (state, obj) {
        // TODO: try catch
        switch (state) {
            case this.productStates.started:
                this.productCurrentRequestState.active = true;
                break;
            case this.productStates.completed_sucessfully:
            case this.productStates.completed_error:
            default:
                this.productCurrentRequestState.active = false;
        }
        // alert every observer
        for (var x = 0; x < this.productStateWatchers.length; x++) {
            this.productStateWatchers[x](state, obj); // call the watcher function
        }
    };
    /**
     * registerProductErrorEvent - Allow consumers to register for notifications when an error occurs doing a product request
     * @param {function} notifyCallback
     * @return {function} deregisterSearchErrorEvent
     */
    ProductService.prototype.registerProductErrorEvent = function (notifyCallback) {
        var self = this;
        // add the observers to our list
        this.productErrorWatchers.push(notifyCallback);
        // remove the observers from our list when called, make sure to do this in destructors
        return function deregisterProductErrorEvent() {
            var index = self.productErrorWatchers.indexOf(notifyCallback);
            if (index !== -1) {
                self.productErrorWatchers.splice(index, 1);
            }
        };
    };
    ;
    /**
     * SearchErrorEvent -  Fires a error event.
     * @param {object} errorType
     * @param {object} obj
     * @constructor
     */
    ProductService.prototype.ProductErrorEvent = function (errorType, obj) {
        // TODO: try catch
        // alert every observer
        for (var x = 0; x < this.productErrorWatchers.length; x++) {
            this.productErrorWatchers[x](errorType, obj); // call the watcher function
        }
    };
    /**
     * Makes request to get a different page of data
     * @param {number} productId
     * @return {promise}
     */
    ProductService.prototype.getAlternateProductsDetails = function (productId) {
        var _this = this;
        return this.$http.get('api/products/' + productId + '/alternates')
            .then(function (results) {
            if (results.data.errors.length > 0) {
                // API level errors here. Still needs more definition
                _this.ProductStateEvent(_this.productStates.completed_error);
                _this.ProductErrorEvent(_this.productErrorTypes.error_give_up, results.data.errors[0]);
                return _this.$q.reject(results.data.errors[0]);
            }
            var productModel = createProductsModel.call(_this, results.data.response);
            _this.ProductStateEvent(_this.productStates.completed_sucessfully);
            if (productModel && productModel.items) {
                productModel.items.forEach(function (item) {
                    if (!item.restrictedMessage) {
                        item.restrictedMessage = 'This item is restricted to Qualified Healthcare Practitioners.';
                    }
                });
            }
            return productModel;
        })
            .catch(function (error) {
            // Request level errors here. Still needs more definition
            _this.ProductStateEvent(_this.productStates.completed_error);
            _this.SearchErrorEvent(_this.searchErrorTypes.error_can_try_again, error);
            return _this.$q.reject(error);
        });
    };
    /**
     * Makes request to get a different page of data
     * @param {number} productId
     * @return {promise}
     */
    ProductService.prototype.getProductDetails = function (productId) {
        var _this = this;
        this.ProductStateEvent(this.productStates.started);
        return this.$http.get('api/products/' + productId)
            .then(function (results) {
            if (results.data.errors.length > 0) {
                // API level errors here. Still needs more definition
                _this.ProductStateEvent(_this.productStates.completed_error);
                _this.ProductErrorEvent(_this.productErrorTypes.error_give_up, results.data.errors[0]);
                return _this.$q.reject(results);
            }
            var productModel = createProductsModel.call(_this, results.data.response);
            _this.ProductStateEvent(_this.productStates.completed_sucessfully);
            if (productModel && productModel.items) {
                productModel.items.forEach(function (item) {
                    if (!item.restrictedMessage) {
                        item.restrictedMessage = 'This item is restricted to Qualified Healthcare Practitioners.';
                    }
                });
            }
            return productModel;
        })
            .catch(function (error) {
            // Request level errors here. Still needs more definition
            return _this.$q.reject(error);
        });
    };
    /**
     * Generic method for this service that makes an http request to the specified url and caches the results
     * @param {String} url
     * @return {promise}
     */
    ProductService.prototype.requestFacetData = function (url) {
        var _this = this;
        return this.$http.get(url, { cache: true })
            .then(function (response) {
            if (response.data && response.data.response) {
                return response.data.response;
            }
            else {
                return _this.$q.reject(response);
            }
        })
            .catch(function (error) {
            return _this.$q.reject(error);
        });
    };
    /**
     * Get all brands
     * @return {promise}
     */
    ProductService.prototype.getBrands = function () {
        return this.requestFacetData('api/products/brands');
    };
    /**
     * Get all categories
     * @return {promise}
     */
    ProductService.prototype.getCategories = function () {
        return this.requestFacetData('api/products/categories');
    };
    /**
     * Get all conditions
     * @return {promise}
     */
    ProductService.prototype.getConditions = function () {
        return this.requestFacetData('api/products/conditions');
    };
    /**
     * Get taxonomies for conditions
     * @return {promise}
     */
    ProductService.prototype.getConditionTaxonomies = function () {
        var _this = this;
        return this.getTaxonomies()
            .then(function (data) {
            return data.filter(function (item) { return item.taxonomyType === 'Condition'; });
        })
            .catch(function () {
            return _this.$q.reject();
        });
    };
    /**
     * Get all delivery formats for all products
     * @return {promise}
     */
    ProductService.prototype.getDeliveryFormats = function () {
        return this.requestFacetData('api/products/deliveryFormats');
    };
    /**
     * Get all dietary considerations for all products
     * @return {promise}
     */
    ProductService.prototype.getDietaryConsiderations = function () {
        return this.requestFacetData('api/products/dietaryConsiderations');
    };
    /**
     * Get all ingredients for all products
     * @return {promise}
     */
    ProductService.prototype.getIngredients = function () {
        return this.requestFacetData('api/products/ingredients');
    };
    /**
     * Get all taxonomies
     * @return {promise}
     */
    ProductService.prototype.getTaxonomies = function () {
        return this.requestFacetData('api/products/taxonomy');
    };
    /**
     * Get all taxonomies (including attributes)
     * @return {promise}
     */
    ProductService.prototype.getTaxonomyMap = function () {
        return this.requestFacetData('api/products/taxonomy-map');
    };
    return ProductService;
}());
export default ProductService;
//# sourceMappingURL=product.service.js.map